import { useState, useEffect, useRef } from "react";
import { Search, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { filterPortalTerms, usePortalTermsCatalog } from "@/hooks/useTerms";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

interface SearchBarProps {
  variant?: "hero" | "compact";
  className?: string;
}

interface Suggestion {
  id: string;
  word: string;
}

export function SearchBar({ variant = "compact", className }: SearchBarProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { data: catalog = [] } = usePortalTermsCatalog();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }
      const rows = filterPortalTerms(catalog, { search: query, limit: 5 });
      setSuggestions(rows.map(t => ({ id: t.id, word: t.word })));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query, catalog]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (searchQuery?: string) => {
    const q = searchQuery || query;
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setShowSuggestions(false);
      setQuery("");
    }
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    navigate(`/term/${suggestion.id}`);
    setShowSuggestions(false);
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSuggestionClick(suggestions[selectedIndex]);
      } else {
        handleSearch();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const isHero = variant === "hero";

  return (
    <div ref={wrapperRef} className={cn("relative w-full", className)}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search
            className={cn(
              "text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2",
              isHero ? "h-5 w-5" : "h-4 w-4"
            )}
          />
          <Input
            ref={inputRef}
            type="text"
            placeholder={t("search.placeholder")}
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setSelectedIndex(-1);
              setShowSuggestions(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            className={cn(
              "text-foreground placeholder:text-muted-foreground focus-visible:ring-primary/50 border-0 bg-white pl-12 pr-4 focus-visible:ring-2",
              isHero
                ? "h-12 rounded-xl text-base shadow-lg"
                : "h-10 rounded-lg text-sm"
            )}
          />
        </div>
        {isHero && (
          <Button
            onClick={() => handleSearch()}
            className="bg-primary hover:bg-primary/90 btn-shimmer h-12 rounded-xl px-6 shadow-lg"
          >
            <Search className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="border-border/50 animate-scale-in absolute top-full z-50 mt-2 w-full overflow-hidden rounded-xl border bg-white shadow-xl">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
              className={cn(
                "group flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left",
                index === selectedIndex
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-secondary text-foreground"
              )}
            >
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-semibold",
                    index === selectedIndex
                      ? "bg-primary/20 text-primary"
                      : "bg-secondary text-muted-foreground"
                  )}
                >
                  {suggestion.word.charAt(0)}
                </div>
                <span className="font-medium">{suggestion.word}</span>
              </div>
              <ArrowRight
                className={cn(
                  "h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100",
                  index === selectedIndex && "opacity-100"
                )}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
